import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import AiChatToolHeader from './AiChatToolHeader';

import type { ReactElement } from 'react';
import type IAiChatToolCard from '../interfaces/IAiChatToolCard';

import styles from '../sass/AiChat.module.scss';

/**
 * The shape a tool with a component of its own draws: the header, under it what the call was made
 * with, under that what the call made when that is worth a card of its own, then the details
 * folded behind a toggle, and last why it failed, if it did. A tool component says what goes in
 * each; how it is laid out lives here alone.
 *
 * The details stay closed until the reader opens them: they are the work of a step, not the reply.
 * A tool whose details are a line or two draws them open instead, with nothing to press. The
 * content is never folded: a run's result card is what the user asked for, and stays in view.
 */
function AiChatToolCard({
  name, status, children, content, details, isDetailsAlwaysOpen = false, errorText,
}: IAiChatToolCard): ReactElement {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const handleToggle = () => setIsExpanded((current) => !current);
  const isDetailsShown = Boolean(details) && (isDetailsAlwaysOpen || isExpanded);

  return (
    <div className={styles.tool}>
      <AiChatToolHeader name={name} status={status} />
      {children ? <div className={styles.toolInput}>{children}</div> : null}
      {content ? <div className={styles.toolContent}>{content}</div> : null}

      {details && !isDetailsAlwaysOpen ? (
        <button type="button" className={styles.toolToggle} onClick={handleToggle}>
          {isExpanded ? t('aiChat.hideDetails') : t('aiChat.showDetails')}
        </button>
      ) : null}
      {isDetailsShown ? <div className={styles.toolDetails}>{details}</div> : null}

      {errorText ? <div className={styles.toolError}>{errorText}</div> : null}
    </div>
  );
}

export default AiChatToolCard;
